import { useState } from 'react';
import { useLegal, LegalSettings, LegalPage, LegalSection, defaultLegalSettings } from '@/context/LegalContext';
import { useTheme } from '../context/ThemeContext';

type LegalTab = 'warranty' | 'terms' | 'kvkk';

export default function LegalAdminTab() {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const { settings, updateSettings, resetSettings } = useLegal();
  const [local, setLocal] = useState<LegalSettings>(settings);
  const [activeTab, setActiveTab] = useState<LegalTab>('warranty');
  const [saved, setSaved] = useState(false);

  const card = dark ? 'bg-[#1C2B3A] border border-white/5' : 'bg-white border border-gray-200';
  const innerCard = dark ? 'bg-[#0D1B2A] border border-white/5' : 'bg-gray-50 border border-gray-100';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const inputCls = dark
    ? 'bg-[#0D1B2A] border-white/10 text-white placeholder-white/30 focus:border-[var(--accent)]'
    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[var(--accent)]';
  const fieldCls = `w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`;
  const labelCls = `block text-xs font-semibold uppercase tracking-wide mb-1.5 ${muted}`;

  const pageKey: Record<LegalTab, keyof LegalSettings> = {
    warranty: 'warranty',
    terms: 'terms',
    kvkk: 'kvkk',
  };

  const pageTabs: { key: LegalTab; label: string; icon: string; path: string }[] = [
    { key: 'warranty', label: 'Warranty Policy', icon: 'ri-shield-check-line', path: '/warranty' },
    { key: 'terms', label: 'Terms of Use', icon: 'ri-file-text-line', path: '/terms' },
    { key: 'kvkk', label: 'Privacy Policy', icon: 'ri-lock-line', path: '/privacy' },
  ];

  const currentPage = local[pageKey[activeTab]] as LegalPage;

  const updatePage = (key: keyof LegalPage, value: string) => {
    setLocal((prev) => ({
      ...prev,
      [pageKey[activeTab]]: { ...prev[pageKey[activeTab]], [key]: value },
    }));
  };

  const updateSection = (i: number, field: keyof LegalSection, value: string) => {
    setLocal((prev) => {
      const page = { ...prev[pageKey[activeTab]] } as LegalPage;
      const sections = [...page.sections];
      sections[i] = { ...sections[i], [field]: value };
      return { ...prev, [pageKey[activeTab]]: { ...page, sections } };
    });
  };

  const addSection = () => {
    setLocal((prev) => {
      const page = { ...prev[pageKey[activeTab]] } as LegalPage;
      return {
        ...prev,
        [pageKey[activeTab]]: {
          ...page,
          sections: [...page.sections, { heading: 'New Section', body: 'Enter content here.' }],
        },
      };
    });
  };

  const removeSection = (i: number) => {
    setLocal((prev) => {
      const page = { ...prev[pageKey[activeTab]] } as LegalPage;
      return {
        ...prev,
        [pageKey[activeTab]]: {
          ...page,
          sections: page.sections.filter((_, idx) => idx !== i),
        },
      };
    });
  };

  const moveSection = (i: number, dir: -1 | 1) => {
    setLocal((prev) => {
      const page = { ...prev[pageKey[activeTab]] } as LegalPage;
      const sections = [...page.sections];
      const target = i + dir;
      if (target < 0 || target >= sections.length) return prev;
      [sections[i], sections[target]] = [sections[target], sections[i]];
      return { ...prev, [pageKey[activeTab]]: { ...page, sections } };
    });
  };

  const handleSave = () => {
    updateSettings(local);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = () => {
    resetSettings();
    setLocal(defaultLegalSettings);
  };

  return (
    <div className="space-y-5">
      {/* Page selector */}
      <div className={`flex gap-1 p-1 rounded-lg ${dark ? 'bg-white/5' : 'bg-gray-100'}`}>
        {pageTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap flex-1 justify-center ${
              activeTab === t.key
                ? 'text-white'
                : dark ? 'text-white/50 hover:text-white' : 'text-gray-500 hover:text-gray-900'
            }`}
            style={activeTab === t.key ? { background: 'var(--accent)' } : {}}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={t.icon}></i>
            </div>
            {t.label}
          </button>
        ))}
      </div>

      {/* Page meta */}
      <div className={`${card} rounded-lg p-6 space-y-4`}>
        <div className="flex items-center justify-between mb-2">
          <h2 className={`font-bold text-sm ${text}`}>Page Settings</h2>
          <a
            href={pageTabs.find((t) => t.key === activeTab)?.path}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-1.5 text-xs font-semibold cursor-pointer transition-colors ${muted} hover:text-[var(--accent)]`}
          >
            <i className="ri-external-link-line"></i>
            Preview page
          </a>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Page Title</label>
            <input className={fieldCls} value={currentPage.title} onChange={(e) => updatePage('title', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Last Updated Date</label>
            <input type="date" className={fieldCls} value={currentPage.lastUpdated} onChange={(e) => updatePage('lastUpdated', e.target.value)} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Subtitle / Description</label>
          <input className={fieldCls} value={currentPage.subtitle} onChange={(e) => updatePage('subtitle', e.target.value)} />
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className={`font-bold text-sm ${text}`}>
            Sections <span className={`font-normal ${muted}`}>({currentPage.sections.length})</span>
          </h2>
          <button
            onClick={addSection}
            className="flex items-center gap-2 text-white text-xs font-bold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            style={{ background: 'var(--accent)' }}
          >
            <i className="ri-add-line"></i>
            Add Section
          </button>
        </div>

        {currentPage.sections.map((section, i) => (
          <div key={i} className={`${card} rounded-lg p-5`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span
                  className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-black text-white shrink-0"
                  style={{ background: 'var(--accent)' }}
                >
                  {i + 1}
                </span>
                <span className={`text-sm font-bold ${text}`}>{section.heading || 'Untitled Section'}</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => moveSection(i, -1)}
                  disabled={i === 0}
                  className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${dark ? 'hover:bg-white/10 text-white/30' : 'hover:bg-gray-100 text-gray-400'} disabled:opacity-20`}
                >
                  <i className="ri-arrow-up-s-line text-sm"></i>
                </button>
                <button
                  onClick={() => moveSection(i, 1)}
                  disabled={i === currentPage.sections.length - 1}
                  className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${dark ? 'hover:bg-white/10 text-white/30' : 'hover:bg-gray-100 text-gray-400'} disabled:opacity-20`}
                >
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </button>
                <button
                  onClick={() => removeSection(i)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Section Heading</label>
                <input
                  className={fieldCls}
                  value={section.heading}
                  onChange={(e) => updateSection(i, 'heading', e.target.value)}
                  placeholder="Section title..."
                />
              </div>
              <div>
                <label className={labelCls}>Content</label>
                <textarea
                  rows={4}
                  className={fieldCls}
                  value={section.body}
                  onChange={(e) => updateSection(i, 'body', e.target.value)}
                  placeholder="Section content..."
                  maxLength={2000}
                />
                <p className={`text-xs mt-1 ${muted}`}>{section.body.length}/2000 characters</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Save */}
      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          onClick={handleReset}
          className={`flex items-center gap-2 font-bold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm border ${dark ? 'border-white/10 text-white/40 hover:text-white/70 hover:border-white/20' : 'border-gray-200 text-gray-400 hover:text-gray-600'}`}
        >
          <i className="ri-refresh-line"></i>
          Reset to Default
        </button>
        <div className="flex items-center gap-3">
          {saved && (
            <div className="flex items-center gap-2 text-green-400 text-sm font-semibold">
              <i className="ri-check-line"></i>
              Saved &amp; published!
            </div>
          )}
          <button
            onClick={handleSave}
            className="flex items-center gap-2 text-white font-bold px-6 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm"
            style={{ background: 'var(--accent)' }}
          >
            <i className="ri-save-line"></i>
            Save &amp; Publish
          </button>
        </div>
      </div>
    </div>
  );
}
